import React, { Fragment } from "react";
import PropTypes from "prop-types";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  FilterFn,
  flexRender,
} from "@tanstack/react-table";

//Import Flatepicker
import Flatpickr from "react-flatpickr";

import {
  Table,
  Row,
  Col,
  Button,
  InputGroup,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
} from "reactstrap";
import { Filter, DefaultColumnFilter } from "./filters";

interface GlobalFilterProps {
  preGlobalFilteredRows: any;
  globalFilter?: any;
  setGlobalFilter: any;
}

const normalizeSearchValue = (value: unknown) =>
  String(value ?? "").toLowerCase();

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}: GlobalFilterProps) {
  const [value, setValue] = React.useState(normalizeSearchValue(globalFilter));

  const onChange = (e: any) => {
    const nextValue = normalizeSearchValue(e.target.value);
    setGlobalFilter(nextValue || undefined);
    setValue(nextValue);
  };

  return (
    <div className="d-inline-block">
      <input
        onChange={(e) => onChange(e)}
        id="search-bar-0"
        type="text"
        className="form-control font-size-13"
        value={value || ""}
        aria-label="Search this table"
      />
    </div>
  );
}

const normalizeFilterValue = (value: unknown) =>
  normalizeSearchValue(value)
    .trim()
    .toLowerCase();

const removePunctuation = (value: string) => value.replace(/[^a-z0-9]/g, "");

const removeNonDigits = (value: string) => value.replace(/\D/g, "");

const punctuationInsensitiveGlobalFilter: FilterFn<any> = (
  row,
  columnId,
  filterValue,
) => {
  const search = normalizeFilterValue(filterValue);
  if (!search) return true;

  const value = normalizeFilterValue(row.getValue(columnId));
  if (value.includes(search)) return true;

  const searchWithoutPunctuation = removePunctuation(search);
  const valueWithoutPunctuation = removePunctuation(value);
  if (
    searchWithoutPunctuation &&
    valueWithoutPunctuation.includes(searchWithoutPunctuation)
  ) {
    return true;
  }

  const searchDigits = removeNonDigits(search);
  return Boolean(searchDigits && removeNonDigits(value).includes(searchDigits));
};

interface TableContainerProps {
  columns: ColumnDef<any>[];
  data: any;
  isGlobalFilter?: any;
  isAddOptions?: any;
  isAddUserList?: any;
  isAddInvoiceList?: any;
  handleOrderClicks?: any;
  handleUserClick?: any;
  handleCustomerClick?: any;
  isAddCustList?: any;
  customPageSize: any;
  className?: any;
  handleInvoiceClick?: any;
  theadClass?: any;
  isBordered?: boolean;
  hidePagination?: boolean;
  onRowClick?: (row: any) => void;
  getRowClassName?: (row: any) => string;
  globalFilterChildren?: React.ReactNode;
}

const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  isAddOptions,
  isAddInvoiceList,
  handleOrderClicks,
  handleUserClick,
  handleCustomerClick,
  isAddCustList,
  customPageSize,
  className,
  handleInvoiceClick,
  theadClass,
  isBordered,
  hidePagination,
  onRowClick,
  getRowClassName,
  globalFilterChildren,
}: TableContainerProps) => {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [pagination, setPagination] = React.useState({
    pageIndex: 0, //initial page index
    pageSize: customPageSize, //default page size
  });
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      pagination,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: punctuationInsensitiveGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    defaultColumn: {
      cell: (info) => info.getValue(),
      header: (info) => info.column.id,
    },
  });

  const generateSortingIndicator = (column: any) => {
    return column.getIsSorted() ? (
      column.getIsSorted() === "desc" ? (
        <span>&#9650;</span>
      ) : (
        <span>&#9660;</span>
      )
    ) : (
      ""
    );
  };

  const onChangeInSelect = (event: any) => {
    table.setPageSize(Number(event.target.value));
  };

  const onChangeInInput = (event: any) => {
    const page = event.target.value ? Number(event.target.value) - 1 : 0;
    table.setPageIndex(page);
  };

  return (
    <Fragment>
      <>
        {isAddInvoiceList && (
          <Row>
            <div className="col-sm">
              <div className="mb-4">
                <button
                  onClick={handleInvoiceClick}
                  type="button"
                  className="btn btn-light waves-effect waves-light"
                >
                  <i className="bx bx-plus me-1"></i> Add Invoice
                </button>
              </div>
            </div>
            <div className="col-sm-auto">
              <div className="d-flex align-items-center gap-1 mb-4">
                <InputGroup className="datepicker-range">
                  <Flatpickr
                    className="form-control d-block"
                    options={{
                      altInput: true,
                      altFormat: "F j, Y",
                      dateFormat: "Y-m-d",
                    }}
                  />
                  <button className="input-group-text" id="date1">
                    <i className="bx bx-calendar-event"></i>
                  </button>
                </InputGroup>
                <UncontrolledDropdown>
                  <DropdownToggle
                    className="btn btn-link text-muted py-1 font-size-16 shadow-none dropdown-toggle"
                    tag="a"
                  >
                    <i className="bx bx-dots-horizontal-rounded"></i>
                  </DropdownToggle>
                  <DropdownMenu className="dropdown-menu-end">
                    <DropdownItem>Action</DropdownItem>
                    <DropdownItem>Another action</DropdownItem>
                    <DropdownItem>Something else here</DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </div>
            </div>
          </Row>
        )}
        <Row>
          {!hidePagination && (
            <Col sm={12} md={6}>
              <label className="d-flex">
                <span className="pt-1 fw-normal">Mostrar</span>
                <Col lg={1} className="ms-1 me-1" style={{ minWidth: "65px" }}>
                  <select
                    className="custom-select me-2 pe-3 custom-select-sm form-control form-control-sm form-select form-select-sm"
                    value={table.getState().pagination.pageSize}
                    onChange={onChangeInSelect}
                  >
                    {[50, 75, 100, 150].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        {" "}
                        {pageSize}
                      </option>
                    ))}
                  </select>
                </Col>{" "}
                <span className="pt-1 fw-normal">registros</span>
              </label>
            </Col>
          )}

          {isGlobalFilter && (
            <Col sm={12} md={6}>
              <div className="d-flex justify-content-end align-items-center gap-2">
                <label className="d-flex align-items-center justify-content-end fw-normal mb-0 flex-grow-1">
                  <span className="me-2">Buscar:</span>{" "}
                  <GlobalFilter
                    preGlobalFilteredRows={table.getPreFilteredRowModel().rows}
                    globalFilter={globalFilter}
                    setGlobalFilter={setGlobalFilter}
                  />
                </label>
                {globalFilterChildren}
              </div>
            </Col>
          )}
        </Row>
        {isAddOptions && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                className="btn-rounded mb-2 me-2"
                onClick={handleOrderClicks}
              >
                <i className="mdi mdi-plus me-1" />
                Add New Order
              </Button>
            </div>
          </Col>
        )}
        {isAddCustList && (
          <Col sm="12">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                className="btn-rounded mb-2 me-2"
                onClick={handleCustomerClick}
              >
                <i className="mdi mdi-plus me-1" />
                New Customers
              </Button>
            </div>
          </Col>
        )}
      </>

      <div className="table-responsive">
        <Table
          hover
          {...{
            role: "table",
          }}
          className={className}
          bordered={isBordered}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} colSpan={header.colSpan}>
                    <div
                      {...{
                        className: header.column.getCanSort()
                          ? "cursor-pointer select-none"
                          : "",
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                      {generateSortingIndicator(header.column)}
                    </div>
                    {header.column.getCanFilter() ? (
                      <div>
                        <Filter column={header.column} />
                      </div>
                    ) : null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={
                  onRowClick ? () => onRowClick(row.original) : undefined
                }
                className={
                  onRowClick
                    ? `cursor-pointer ${getRowClassName?.(row.original) ?? ""}`.trim()
                    : undefined
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <Row className="align-items-center g-3 text-center text-sm-start">
        <div className="col-sm">
          <div>
            Mostrando{" "}
            <span className="fw-normal">{table.getRowModel().rows.length}</span>{" "}
            de <span className="fw-normal">{data.length}</span> resultados
          </div>
        </div>
        {!hidePagination && (
          <div className="col-sm-auto">
            <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
              <li
                className={
                  !table.getCanPreviousPage()
                    ? "page-item disabled"
                    : "page-item"
                }
              >
                <button
                  type="button"
                  className="page-link"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  Anterior
                </button>
              </li>
              {Array.from({ length: table.getPageCount() }).map((_, index) => (
                <React.Fragment key={index}>
                  <li className="page-item">
                    <button
                      type="button"
                      className={
                        table.getState().pagination.pageIndex === index
                          ? "page-link active"
                          : "page-link"
                      }
                      onClick={() => table.setPageIndex(index)}
                    >
                      {index + 1}
                    </button>
                  </li>
                </React.Fragment>
              ))}
              <li
                className={
                  !table.getCanNextPage() ? "page-item disabled" : "page-item"
                }
              >
                <button
                  type="button"
                  className="page-link"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  Siguiente
                </button>
              </li>
            </ul>
          </div>
        )}
      </Row>
    </Fragment>
  );
};

TableContainer.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

export default TableContainer;
